import React from 'react';

import HomePage from './app/pages/index';
import LoginPage from './app/pages/auth/LoginPage';
import ForgotPasswordPage from './app/pages/auth/ForgotPasswordPage';
import DefaultLayout from './app/layouts/DefaultLayout';
import ResetPasswordPage from './app/pages/auth/ResetPasswordPage';
import DashboardPage from '@pages/DashboardPage';
import BadgePage from './app/pages/badges';
import ErrorPage from '@pages/error/ErrorPage';
import WorksheetQuestionPage from '@pages/worksheet/WorksheetQuestionPage';
import GenieclassRecordedLessonPage from '@pages/online_lesson/GenieclassRecordedLessonPage';
import CreateWorksheetPage from '@pages/worksheet/CreateWorksheetPage';
import StarterWorksheetPage from '@pages/worksheet/StarterWorksheetPage';
import CustomWorksheetPage from '@pages/worksheet/CustomWorksheetPage';
import CreateRevisionWorksheetPage from '@pages/worksheet/CreateRevisionWorksheetPage';
import GenieclassQuizPage from '@pages/online_lesson/GenieclassQuizPage';
import ChangeLog from '@pages/changelog';
import ParentPage from '@pages/parent/ParentPage';
import Fullchat from '@pages/genieask/Fullchatpage';
import GenieclassReportPage from '@pages/online_lesson/GenieclassReportPage';
import MockExamRegistration from '@pages/mock_exam/MockExamRegistration';
import WebinarRegistration from '@pages/mock_exam/WebinarRegistration';
import LeaderboardDashboardPage from '@pages/leaderboard/LeaderboardDashboardPage';
import ProfilePage from '@pages/profile/ProfilePage';
import BasicInfoPage from '@pages/profile/BasicInfoPage';
import AcademicPage from '@pages/profile/AcademicPage';
import AccountSettingPage from '@pages/settings/AccountSettingPage';
import EditAccountPage from '@pages/settings/EditAccountPage';
import ChangePasswordPage from '@pages/settings/ChangePasswordPage';
import ParentQrCode from '@components/parent/ParentQrCode';
import ParentCampusReport from '@components/parent/ParentCampusReport';
import WorksheetCompletedDetailPage from '@pages/worksheet/WorksheetCompletedDetailPage';
import SubscriptionBase from '@pages/subscription/SubscriptionBase';
import SubscriptionDashboardPage from '@pages/subscription/SubscriptionDashboardPage';
import SubscriptionDetailPage from '@pages/subscription/SubscriptionDetailPage';
import SubscriptionPlanOverviewPage from '@pages/subscription/SubscriptionPlanOverviewPage';
import AccountRouteLayout from '@layouts/AccountRouteLayout';
import WorksheetCompletedPDFPage from '@pages/worksheet/WorksheetCompletedPDFPage';
import ExamResultDashboardPage from '@pages/exam-result/ExamResultDashboardPage';
import AddExamResultPage from '@pages/exam-result/AddExamResultPage';
import EditExamResultPage from '@pages/exam-result/EditExamresultPage';
import GenieclassStreamPage from '@pages/online_lesson/GenieclassStreamPage';

export interface ISiteRouteItem {
  path: string;
  element: React.ReactNode;
  children?: ISiteRouteItem[];
  without_layout?: boolean;
}

export type ISiteRouteProps = ISiteRouteItem[];

/**
 * ROUTES that accessible for public/guest
 */
export const SITE_ROUTES: ISiteRouteProps = [
  {
    path: '/homepage',
    element: (
      <DefaultLayout>
        <HomePage />
      </DefaultLayout>
    ),
  },
  {
    path: '/',
    element: (
      <DefaultLayout className={'!h-fit'}>
        <LoginPage />
      </DefaultLayout>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <DefaultLayout className={'!h-fit'}>
        <ForgotPasswordPage />
      </DefaultLayout>
    ),
  },
  {
    path: '/login/password_reset/:token',
    element: (
      <DefaultLayout className={'!h-fit'}>
        <ResetPasswordPage />
      </DefaultLayout>
    ),
  },
  // Internal Server Error
  {
    path: '/500',
    element: (
      <DefaultLayout>
        <ErrorPage code="500" />
      </DefaultLayout>
    ),
  },
  {
    path: '/404',
    element: (
      <DefaultLayout>
        <ErrorPage code="404" />
      </DefaultLayout>
    ),
  },
  {
    path: '/changelog',
    element: (
      <DefaultLayout>
        <ChangeLog />
      </DefaultLayout>
    ),
  },
  {
    path: '/mock-exams',
    element: <MockExamRegistration />,
  },
  {
    path: '/mockexamswebinars/:id',
    element: <WebinarRegistration />,
  },
];

/**
 * ROUTES that require logged in users
 */
export const AUTH_ROUTES: ISiteRouteProps = [
  // dashboard page
  {
    // example route for math post quiz: http://localhost:3000/mathematics/quiz/3/22156
    path: '/:subjectName/quiz/:quizType/:onlineClassId',
    element: <GenieclassQuizPage />,
    without_layout: true,
  },
  {
    path: '/parent',
    element: <ParentPage />,
    without_layout: true,
    children: [
      {
        path: '',
        element: <ParentQrCode />,
        without_layout: true,
      },
      {
        path: ':student_id/:subject_id',
        element: <ParentCampusReport />,
        without_layout: true,
      },
    ],
  },
  {
    path: '/:subjectId/:menuId',
    element: <DashboardPage />,
  },
  // create worksheet page
  {
    path: '/:subjectId/worksheets/create',
    element: <CreateWorksheetPage />,
  },
  // create starter worksheet page
  {
    path: '/:subjectId/worksheets/create/starter',
    element: <StarterWorksheetPage />,
  },
  // create custom worksheet page
  {
    path: '/:subjectId/worksheets/create/customised',
    element: <CustomWorksheetPage />,
  },
  //Create revision worksheet page
  {
    path: '/:subjectId/worksheets/create/revision',
    element: <CreateRevisionWorksheetPage />,
  },
  // Delayed Start Subscription
  {
    path: '/:subjectId/099-03/:delayedStartDate',
    element: (
      <DefaultLayout>
        <ErrorPage code="099-03" />
      </DefaultLayout>
    ),
    without_layout: true,
  },

  // geniesmart page
  {
    path: '/:subjectId/worksheets/worksheet_question/:worksheetId',
    element: <WorksheetQuestionPage />,
    without_layout: true,
  },
  {
    path: '/:subjectId/worksheets/completed/:worksheetId',
    element: <WorksheetCompletedDetailPage />,
  },
  {
    path: '/:subjectId/worksheets/completed-pdf/:worksheetId',
    element: <WorksheetCompletedPDFPage />,
    without_layout: true,
  },

  // genieclass page
  {
    path: '/:subjectId/classes/recorded/:onlineLessonId',
    element: <GenieclassRecordedLessonPage />,
  },
  {
    path: '/:subjectId/classes/report/:onlineLessonId',
    element: <GenieclassReportPage />,
  },
  {
    path: '/:subjectName/classes/stream/:onlineClassId',
    element: <GenieclassStreamPage />,
    without_layout: true,
  },
  {
    path: '/genieask',
    element: <Fullchat />,
    without_layout: false,
  },

  {
    path: '/',
    element: <AccountRouteLayout />,
    children: [
      {
        path: '/subscription',
        element: <SubscriptionBase />,
        without_layout: true,
        children: [
          {
            path: '',
            element: <SubscriptionDashboardPage />,
            without_layout: true,
          },
          {
            path: ':subject',
            element: <SubscriptionDetailPage />,
            without_layout: true,
          },
          {
            path: ':subject/plan-overview',
            element: <SubscriptionPlanOverviewPage />,
            without_layout: true,
          },
        ],
      },
      // badge page from profile menu badge
      {
        path: '/badges/',
        without_layout: true,
        element: <BadgePage />,
      },
      {
        path: '/leaderboard/',
        without_layout: true,
        element: <LeaderboardDashboardPage />,
      },
      {
        path: '/exam-result/',
        without_layout: true,
        element: <ExamResultDashboardPage />,
      },
      {
        path: '/exam-result/add',
        without_layout: true,
        element: <AddExamResultPage />,
      },
      {
        path: '/exam-result/edit/:examResultId',
        without_layout: true,
        element: <EditExamResultPage />,
      },

      // profile section
      {
        path: '/profile/',
        without_layout: true,
        element: <ProfilePage />,
      },
      {
        path: '/profile/update-basic-info',
        without_layout: true,
        element: <BasicInfoPage />,
      },
      {
        path: '/profile/update-academic',
        without_layout: true,
        element: <AcademicPage />,
      },

      // setting section
      {
        path: '/account-settings/',
        without_layout: true,
        element: <AccountSettingPage />,
      },
      {
        path: '/account-settings/update-account',
        without_layout: true,
        element: <EditAccountPage />,
      },
      {
        path: '/account-settings/change-password',
        without_layout: true,
        element: <ChangePasswordPage />,
      },
    ],
  },
];
